import React, { useState, useEffect } from 'react';
import { Settings, Save, Cloud, Users, Bell, Database, Globe, Download, Upload, Mail, Key, CheckCircle, XCircle, Link as LinkIcon, UserPlus, Send, MapPin, Plus, Trash2, Sparkles, ExternalLink, CreditCard, Star } from 'lucide-react';
import DevicesPanel from '../components/DevicesPanel';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { carsysApi } from '../api/carsysApi';
import TalenManager from '../components/TalenManager';
import SuggestiesManager from '../components/SuggestiesManager';
import axios from 'axios';
import { toast } from 'sonner';
import Pricing from './Pricing';
import { confirmDialog } from '../lib/confirm';

import { API_BASE as BASE } from '../lib/apiBase';

export default function Instellingen() {

  const { t } = useT();  const { backupUrl, getMailLog, refreshMeldingen, gegevens, updateGegevens, refreshAll, refreshStats } = useData();
  const { user } = useAuth();
  const { lang, setLang, languages } = useT();
  const [tab, setTab] = useState(() => {
    const want = sessionStorage.getItem('mc_open_settings_tab');
    if (want) { sessionStorage.removeItem('mc_open_settings_tab'); return want; }
    return 'algemeen';
  });
  const [cloudStatus, setCloudStatus] = useState({ enabled: false, licenseActive: false, providersAvailable: { dropbox: false } });
  const [licenseKey, setLicenseKey] = useState('');
  const [mailLog, setMailLog] = useState([]);
  const [team, setTeam] = useState([]);
  const [smtp, setSmtp] = useState({ host: 'smtp.gmail.com', port: 587, user: '', password: '', from_name: '', use_tls: true });
  const [invite, setInvite] = useState({ email: '', password: '', naam: '' });
  const [betaling, setBetaling] = useState({ stripeOnlineBetalen: false, stripeKeyConfigured: false });
  const [stripeKeyInput, setStripeKeyInput] = useState('');
  const [betalingBusy, setBetalingBusy] = useState(false);
  const [review, setReview] = useState({ googleReviewUrl: '', autoReview: false, reviewDagen: 7 });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [weekly, setWeekly] = useState({ enabled: true, lastSent: null });
  const [algemeenDirty, setAlgemeenDirty] = useState(null);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const algemeen = algemeenDirty || {
    taal: lang,
    btwPercentage: gegevens?.btwPercentage ?? 21,
    uurtarief: gegevens?.uurtarief ?? 75,
    hobbyMode: gegevens?.hobbyMode || false,
  };
  const setAlgemeen = (v) => {
    const next = typeof v === 'function' ? v(algemeen) : v;
    setAlgemeenDirty(next);
    // Taal toggle direct toepassen (geen save nodig)
    if (next.taal && next.taal !== lang) setLang(next.taal);
  };

  const saveAlgemeen = async () => {
    try {
      await updateGegevens({ ...gegevens, ...algemeen });
      setAlgemeenDirty(null);
      toast.success(t('Algemene instellingen opgeslagen'));
    } catch (e) {
      toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  const refreshStatus = () => carsysApi.getCloudStatus().then(setCloudStatus).catch(() => {});
  const refreshTeam = () => axios.get(`${BASE}/api/auth/team`).then(r => setTeam(r.data)).catch(() => {});
  useEffect(() => { refreshStatus(); refreshTeam(); }, []);
  useEffect(() => { if (tab === 'maillog') getMailLog().then(setMailLog).catch(() => {}); }, [tab, getMailLog]);
  useEffect(() => { if (tab === 'betalen') carsysApi.getBetalingInstellingen().then(setBetaling).catch(() => {}); }, [tab]);
  useEffect(() => { if (tab === 'reviews') carsysApi.getReviewInstellingen().then(setReview).catch(() => {}); }, [tab]);
  useEffect(() => { if (tab === 'algemeen') carsysApi.getWeeklySummarySettings().then(setWeekly).catch(() => {}); }, [tab]);
  useEffect(() => {
    const handler = (e) => { if (e.data && e.data.type === 'carsys-cloud-connected') { refreshStatus(); toast.success(t('Cloud verbonden!')); } };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) { toast.error(t('Voer een licentiekey in')); return; }
    try {
      await carsysApi.activateLicense(licenseKey.trim());
      toast.success(t('Licentie geactiveerd! Cloud functies zijn nu beschikbaar.'));
      setLicenseKey('');
      refreshStatus();
      refreshStats();  // ververs plan-gating (slotjes verdwijnen direct)
    } catch (e) {
      toast.error(t('Activatie mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  const handleDeactivateLicense = async () => {
    if (!(await confirmDialog(t('Licentie deactiveren? Cloud functies worden uitgeschakeld.')))) return;
    try { await carsysApi.deactivateLicense(); toast.success(t('Licentie gedeactiveerd')); refreshStatus(); refreshStats(); }
    catch { toast.error(t('Mislukt')); }
  };

  const handleConnectDropbox = async () => {
    if (!cloudStatus.licenseActive) { toast.error(t('Licentie vereist'), { description: t('Activeer eerst een licentie om cloud te koppelen.') }); return; }
    try {
      const { authUrl } = await carsysApi.startDropboxOAuth();
      const popup = window.open(authUrl, 'dropbox-oauth', 'width=600,height=700,scrollbars=yes');
      if (!popup) toast.error(t('Popup geblokkeerd - sta popups toe voor deze site'));
    } catch (e) { toast.error(t('OAuth start mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleConnectGoogle = async () => {
    if (!cloudStatus.licenseActive) { toast.error(t('Licentie vereist'), { description: t('Activeer eerst een licentie om cloud te koppelen.') }); return; }
    try {
      const { authUrl } = await carsysApi.startGoogleOAuth();
      const popup = window.open(authUrl, 'google-oauth', 'width=600,height=700,scrollbars=yes');
      if (!popup) toast.error(t('Popup geblokkeerd - sta popups toe voor deze site'));
    } catch (e) { toast.error(t('OAuth start mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleDisconnect = async () => {
    if (!(await confirmDialog(t('Cloud koppeling verbreken?')))) return;
    try { await carsysApi.disconnectCloud(); toast.success(t('Cloud losgekoppeld')); refreshStatus(); }
    catch { toast.error(t('Mislukt')); }
  };

  const downloadBackup = async () => {
    try {
      const tok = localStorage.getItem('monteurcentraal_token');
      const r = await fetch(backupUrl(), { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monteurcentraal-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('Back-up gedownload'));
    } catch (e) {
      toast.error(t('Back-up mislukt'), { description: e.message });
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.endsWith('.json')) { toast.error(t('Alleen .json bestanden', 'Alleen .json bestanden')); return; }
    let snapshot;
    try {
      const text = await file.text();
      snapshot = JSON.parse(text);
    } catch { toast.error(t('Ongeldig JSON-bestand', 'Ongeldig JSON-bestand')); return; }
    const counts = ['klanten', 'klant_autos', 'bonnen', 'betalingen', 'handelautos', 'planning', 'onderdelen']
      .map(k => `${k}: ${Array.isArray(snapshot[k]) ? snapshot[k].length : 0}`).join('\n');
    const expDate = snapshot._exportDate ? new Date(snapshot._exportDate).toLocaleString('nl-NL') : 'onbekend';
    const ok = await confirmDialog(
      `Backup herstellen?\n\nExport datum: ${expDate}\n\n${counts}\n\n` +
      `LET OP: alle huidige data van jouw garage wordt VERVANGEN door de inhoud van dit bestand. Dit kan niet ongedaan worden gemaakt.`
    );
    if (!ok) return;
    try {
      const r = await carsysApi.backupImport(snapshot);
      const total = Object.values(r.summary || {}).reduce((acc, s) => acc + (s.inserted || 0), 0);
      toast.success(t('Back-up hersteld', `Back-up hersteld - ${total} records geïmporteerd`));
      await refreshAll();
    } catch (err) {
      toast.error(t('Back-up mislukt'), { description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.settings.title')}</h2><p>{t('page.settings.sub')}</p></div>
      </div>

      <div className="cs-tabs">
        {[
          { k: 'algemeen', l: t('tab.general') },
          { k: 'plannen', l: t('tab.plans') },
          { k: 'talen', l: t('tab.languages') },
          { k: 'suggesties', l: t('Suggesties', 'Suggesties') },
          { k: 'licentie', l: (cloudStatus.licenseActive ? '✓ ' : '') + t('tab.license') },
          { k: 'cloud', l: (cloudStatus.enabled ? '✓ ' : '') + t('tab.cloud') },
          { k: 'team', l: t('tab.team') },
          { k: 'smtp', l: t('tab.smtp') },
          { k: 'betalen', l: t('Online betalen', 'Online betalen') },
          { k: 'reviews', l: t('Reviews', 'Reviews') },
          { k: 'backup', l: t('tab.backup') },
          { k: 'meldingen', l: t('tab.notifications') },
          { k: 'maillog', l: t('tab.maillog') },
        ].map(tt => (
          <button data-testid={`tab-${tt.k}`} key={tt.k} className={`cs-tab ${tab === tt.k ? 'active' : ''}`} onClick={() => setTab(tt.k)}>{tt.l}</button>
        ))}
      </div>

      <div className="cs-card padded" style={{ maxWidth: tab === 'plannen' ? 1100 : (tab === 'talen' ? 980 : 820) }}>
        {tab === 'plannen' && (
          <Pricing currentPlan={cloudStatus.plan || 'NONE'} defaultEmail={user?.email} onActivate={() => setTab('licentie')} />
        )}
        {tab === 'talen' && <TalenManager />}
        {tab === 'suggesties' && <SuggestiesManager />}
        {tab === 'algemeen' && (<>
          <h3 className="cs-card-title"><Settings size={16} /> {t('Algemene instellingen', 'Algemene instellingen')}</h3>
          <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 0 }}>{t('Deze instellingen worden gebruikt op alle nieuwe bonnen, offertes en facturen van', 'Deze instellingen worden gebruikt op alle nieuwe bonnen, offertes en facturen van')} <b>{user?.garageNaam}</b>.</p>
          <div className="cs-detail-row">
            <span className="lbl">{t('Taal', 'Taal')}</span>
            <select data-testid="input-taal" className="cs-input" value={algemeen.taal} onChange={e => setAlgemeen({ ...algemeen, taal: e.target.value })} style={{ maxWidth: 240 }}>
              {languages.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
            </select>
          </div>
          <div className="cs-detail-row">
            <span className="lbl">{t('Standaard BTW%', 'Standaard BTW%')}</span>
            <input data-testid="input-btw" type="number" min="0" max="100" step="0.1" className="cs-input" value={algemeen.btwPercentage} onChange={e => setAlgemeen({ ...algemeen, btwPercentage: parseFloat(e.target.value) || 0 })} style={{ maxWidth: 200 }} />
          </div>
          <div className="cs-detail-row">
            <span className="lbl">{t('Uurtarief arbeid (€)', 'Uurtarief arbeid (€)')}</span>
            <input data-testid="input-uurtarief" type="number" min="0" step="0.5" className="cs-input" value={algemeen.uurtarief} onChange={e => setAlgemeen({ ...algemeen, uurtarief: parseFloat(e.target.value) || 0 })} style={{ maxWidth: 200 }} />
          </div>
          <div style={{ marginTop: 18, padding: 14, background: algemeen.hobbyMode ? 'var(--cs-amber-soft)' : 'var(--cs-card-alt)', border: '1px solid var(--cs-border)', borderRadius: 8 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input data-testid="input-hobby" type="checkbox" checked={!!algemeen.hobbyMode} onChange={e => setAlgemeen({ ...algemeen, hobbyMode: e.target.checked, btwPercentage: e.target.checked ? 0 : 21 })} style={{ marginTop: 3 }} />
              <span>
               <b>{t('Hobby modus')}</b> ({t('geen BTW')})
                <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginTop: 4 }}>
                  {t('Voor hobbyisten / niet-BTW-plichtige sleutelaars. Alle nieuwe bonnen worden zonder BTW gemaakt, op de factuur staat "Geen BTW van toepassing". Schakel uit om weer normaal te factureren.', 'Voor hobbyisten / niet-BTW-plichtige sleutelaars. Alle nieuwe bonnen worden zonder BTW gemaakt, op de factuur staat "Geen BTW van toepassing". Schakel uit om weer normaal te factureren.')}
                </div>
              </span>
            </label>
          </div>
          <div style={{ marginTop: 16 }}>
            <button data-testid="btn-save-algemeen" className="cs-btn primary" onClick={saveAlgemeen}><Save size={14} /> {t('Opslaan')}</button>
          </div>

          <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Key size={14} /> {t('Wachtwoord wijzigen', 'Wachtwoord wijzigen')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 12 }}>
              {t('Verander je wachtwoord direct zonder e-mail reset.', 'Verander je wachtwoord direct zonder e-mail reset. Je blijft ingelogd op alle apparaten.')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: 720 }}>
              <div>
                <label className="cs-section-title">{t('Huidig wachtwoord', 'Huidig wachtwoord')}</label>
                <input data-testid="input-current-pwd" type="password" className="cs-input" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} autoComplete="current-password" />
              </div>
              <div>
                <label className="cs-section-title">{t('Nieuw wachtwoord', 'Nieuw wachtwoord')}</label>
                <input data-testid="input-new-pwd" type="password" className="cs-input" value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} minLength={6} autoComplete="new-password" />
              </div>
              <div>
                <label className="cs-section-title">{t('Herhalen', 'Herhalen')}</label>
                <input data-testid="input-new-pwd2" type="password" className="cs-input" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} minLength={6} autoComplete="new-password" />
              </div>
            </div>
            <button data-testid="btn-change-pwd" className="cs-btn warning" style={{ marginTop: 12 }} disabled={pwdBusy} onClick={async () => {
              if (!pwd.current || !pwd.next) { toast.error(t('Vul beide wachtwoorden in', 'Vul huidig en nieuw wachtwoord in')); return; }
              if (pwd.next.length < 6) { toast.error(t('Wachtwoord minimaal 6 tekens')); return; }
              if (pwd.next !== pwd.confirm) { toast.error(t('Wachtwoorden komen niet overeen', 'Wachtwoorden komen niet overeen')); return; }
              setPwdBusy(true);
              try {
                await carsysApi.changePassword(pwd.current, pwd.next);
                toast.success(t('Wachtwoord gewijzigd', 'Wachtwoord gewijzigd'));
                setPwd({ current: '', next: '', confirm: '' });
              } catch (err) {
                toast.error(t('Wijzigen mislukt', 'Wijzigen mislukt'), { description: err.response?.data?.detail || err.message });
              } finally { setPwdBusy(false); }
            }}>
              {pwdBusy ? '...' : <><Key size={14} /> {t('Wachtwoord wijzigen', 'Wachtwoord wijzigen')}</>}
            </button>
          </div>

          <LocatiesManager />

          <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="instellingen-booking-link">
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <LinkIcon size={14} /> {t('Online afspraken — boekingslink', 'Online afspraken — boekingslink')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 12 }}>
              {t('Deel deze link met je klanten zodat zij zelf afspraken kunnen aanvragen. Beheer diensten en openingstijden onder', 'Deel deze link met je klanten zodat zij zelf afspraken kunnen aanvragen. Beheer diensten en openingstijden onder')} <b>{t('Online afspraken', 'Online afspraken')}</b>.
            </div>
            <div style={{ display: 'flex', gap: 6, maxWidth: 720 }}>
              <input data-testid="instellingen-booking-link-input" className="cs-input" readOnly value={`${window.location.origin}/afspraak/${user?.garageId || ''}`} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
              <button data-testid="instellingen-booking-link-copy" className="cs-btn" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/afspraak/${user?.garageId || ''}`).then(() => toast.success(t('Link gekopieerd', 'Link gekopieerd'))); }}><LinkIcon size={14} /> {t('Kopiëren', 'Kopiëren')}</button>
              <a data-testid="instellingen-booking-link-open" className="cs-btn" href={`${window.location.origin}/afspraak/${user?.garageId || ''}`} target="_blank" rel="noreferrer">{t('Openen', 'Openen')}</a>
            </div>
          </div>

          <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="instellingen-weekoverzicht">
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={14} /> {t('Weekoverzicht per e-mail', 'Weekoverzicht per e-mail')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 12 }}>
              {t('Ontvang elke maandagochtend automatisch een samenvatting: omzet van de week, openstaande facturen en APK\'s die binnenkort verlopen.', 'Ontvang elke maandagochtend automatisch een samenvatting: omzet van de week, openstaande facturen en APK\'s die binnenkort verlopen.')}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }} data-testid="toggle-weekoverzicht">
              <input type="checkbox" checked={!!weekly.enabled} style={{ width: 18, height: 18 }}
                onChange={async (e) => {
                  const val = e.target.checked;
                  setWeekly({ ...weekly, enabled: val });
                  try { const r = await carsysApi.setWeeklySummarySettings(val); setWeekly(r); toast.success(t('Opgeslagen', 'Opgeslagen')); }
                  catch (_) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt')); setWeekly({ ...weekly, enabled: !val }); }
                }} />
              {t('Weekoverzicht automatisch versturen', 'Weekoverzicht automatisch versturen')}
            </label>
            {weekly.lastSent && <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginTop: 8 }}>{t('Laatst verstuurd', 'Laatst verstuurd')}: {new Date(weekly.lastSent).toLocaleString()}</div>}
          </div>

          {(() => {
            const srSlug = gegevens?.showroomSlug || '';
            const srUrl = srSlug ? `${window.location.origin}/showroom/${srSlug}` : '';
            return (
              <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="instellingen-showroom-link">
                <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Globe size={14} /> {t('Showroom — publieke link', 'Showroom — publieke link')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 12 }}>
                  {t('Je publieke verkooppagina met je occasions en onderdelen. Publiceer auto\'s/onderdelen onder', 'Je publieke verkooppagina met je occasions en onderdelen. Publiceer auto\'s/onderdelen onder')} <b>{t('Showroom', 'Showroom')}</b>.
                </div>
                {srUrl ? (
                  <div style={{ display: 'flex', gap: 6, maxWidth: 720 }}>
                    <input data-testid="instellingen-showroom-link-input" className="cs-input" readOnly value={srUrl} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
                    <button data-testid="instellingen-showroom-link-copy" className="cs-btn" onClick={() => { navigator.clipboard.writeText(srUrl).then(() => toast.success(t('Link gekopieerd', 'Link gekopieerd'))); }}><LinkIcon size={14} /> {t('Kopiëren', 'Kopiëren')}</button>
                    <a data-testid="instellingen-showroom-link-open" className="cs-btn" href={srUrl} target="_blank" rel="noreferrer">{t('Openen', 'Openen')}</a>
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', fontStyle: 'italic' }} data-testid="instellingen-showroom-link-empty">
                    {t('Nog geen showroom-link. Publiceer eerst een auto of onderdeel in je showroom — daarna verschijnt hier je persoonlijke link.', 'Nog geen showroom-link. Publiceer eerst een auto of onderdeel in je showroom — daarna verschijnt hier je persoonlijke link.')}
                  </div>
                )}
                <ShowroomAdSetting />
                <ShowroomThemePanel />
              </div>
            );
          })()}
        </>)}

        {tab === 'licentie' && (<>
          <h3 className="cs-card-title"><Key size={16} /> {t('Licentie status', 'Licentie status')}</h3>
          {cloudStatus.licenseActive ? (
            <div style={{ padding: 16, background: 'var(--cs-green-soft)', borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--cs-green)' }}>
                <CheckCircle size={18} /> Licentie actief — Plan: <span className="cs-badge blue">{cloudStatus.plan || 'PRO'}</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--cs-text-muted)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <div>{t('Max klanten', 'Max klanten')}: <b>{cloudStatus.planLimits?.max_klanten === 0 ? t('Onbeperkt', 'Onbeperkt') : cloudStatus.planLimits?.max_klanten}</b></div>
                <div>{t('Max bonnen/mnd: ')}<b>{cloudStatus.planLimits?.max_bonnen_per_maand === 0 ? t('Onbeperkt') : cloudStatus.planLimits?.max_bonnen_per_maand}</b></div>
                <div>{t('Max teamleden', 'Max teamleden')}: <b>{cloudStatus.planLimits?.max_seats}</b></div>
                <div>{t('Cloud sync')}: <b>{cloudStatus.planLimits?.cloud ? '✓ ' + t('Ja') : '✗ ' + t('Nee')}</b></div>
                <div>{t('Auto-handel', 'Auto-handel')}: <b>{cloudStatus.planLimits?.auto_handel ? '✓ ' + t('Ja') : '✗ ' + t('Nee')}</b></div>
                <div>{t('Audit log-bewaartermijn', 'Audit log-bewaartermijn')}: <b>{cloudStatus.planLimits?.audit_days === 0 ? t('Onbeperkt', 'Onbeperkt') : `${cloudStatus.planLimits?.audit_days} ${t('dagen', 'dagen')}`}</b></div>
              </div>
              <div data-testid="license-validity" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--cs-green)', fontSize: 12.5, color: 'var(--cs-text)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <div data-testid="license-expiry">
                  {t('Geldig tot', 'Geldig tot')}: <b>
                    {cloudStatus.licensePerpetual
                      ? t('Voor altijd (geen vervaldatum)', 'Voor altijd (geen vervaldatum)')
                      : cloudStatus.licenseExpiresIso
                        ? `${cloudStatus.licenseExpiresIso} (${t('nog', 'nog')} ${cloudStatus.licenseDaysLeft} ${t('dagen', 'dagen')})`
                        : '—'}
                  </b>
                </div>
                <div data-testid="license-email">
                  {t('Gekoppeld aan', 'Gekoppeld aan')}: <b>{cloudStatus.licenseEmail || '—'}</b>
                </div>
                {cloudStatus.licenseActivatedAt && (
                  <div>{t('Geactiveerd op', 'Geactiveerd op')}: <b>{cloudStatus.licenseActivatedAt}</b></div>
                )}
                {!cloudStatus.licensePerpetual && cloudStatus.licenseDaysLeft != null && cloudStatus.licenseDaysLeft <= 14 && (
                  <div style={{ gridColumn: '1 / 3', color: 'var(--cs-amber)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    ⚠ {t('Je licentie verloopt binnenkort. Verleng op tijd om onderbreking te voorkomen.', 'Je licentie verloopt binnenkort. Verleng op tijd om onderbreking te voorkomen.')}
                    <button className="cs-btn primary" data-testid="btn-verleng-soon" onClick={() => setTab('plannen')}>{t('Nu verlengen →', 'Nu verlengen →')}</button>
                  </div>
                )}
              </div>
              <button className="cs-btn danger" style={{ marginTop: 12 }} onClick={handleDeactivateLicense}>{t('Licentie deactiveren', 'Licentie deactiveren')}</button>
            </div>
          ) : (
            <div style={{ padding: 16, background: 'var(--cs-amber-soft)', borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--cs-amber)' }}>
                <XCircle size={18} /> {cloudStatus.licenseExpired
                  ? t('Je licentie is verlopen — BASIC mode (gratis)', 'Je licentie is verlopen — BASIC mode (gratis)')
                  : t('Geen actieve licentie — BASIC mode (gratis)', 'Geen actieve licentie — BASIC mode (gratis)')}
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--cs-text-muted)' }}>
                {t('BASIC limieten actief: max 50 klanten · 30 bonnen/maand · 1 gebruiker · geen cloud · geen auto-handel.', 'BASIC limieten actief: max 50 klanten · 30 bonnen/maand · 1 gebruiker · geen cloud · geen auto-handel.')}
                {' '}{t('Upgrade naar PRO of ENTERPRISE voor onbeperkt gebruik + cloud sync.', 'Upgrade naar PRO of ENTERPRISE voor onbeperkt gebruik + cloud sync.')}
              </div>
              <button className="cs-btn primary" style={{ marginTop: 12 }} data-testid="btn-verleng-koop" onClick={() => setTab('plannen')}>
                {cloudStatus.licenseExpired ? t('Verleng licentie →', 'Verleng licentie →') : t('Verleng / koop licentie →', 'Verleng / koop licentie →')}
              </button>
            </div>
          )}
          <label className="cs-section-title">{t('Licentiekey invoeren', 'Licentiekey invoeren')}</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="cs-input" placeholder={t('MC-PRO-... (lange cryptografisch ondertekende key)', 'MC-PRO-... (lange cryptografisch ondertekende key)')} value={licenseKey} onChange={e => setLicenseKey(e.target.value)} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }} />
            <button className="cs-btn primary" onClick={handleActivateLicense} style={{ whiteSpace: 'nowrap' }}>{t('Activeren')}</button>
          </div>
          <div style={{ marginTop: 12, padding: 12, background: 'var(--cs-card-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--cs-text-muted)' }}>
            <b>{t('Geen licentie?', 'Geen licentie?')}</b> {t('Licenties worden cryptografisch ondertekend door MonteurCentraal en zijn niet te vervalsen.', 'Licenties worden cryptografisch ondertekend door MonteurCentraal en zijn niet te vervalsen.')}
            {' '}{t('Een licentie wordt gebonden aan jouw garage bij activatie - hergebruik door een andere garage wordt geblokkeerd.', 'Een licentie wordt gebonden aan jouw garage bij activatie - hergebruik door een andere garage wordt geblokkeerd.')}<br />
            <span style={{ marginTop: 4, display: 'inline-block' }}><b>{t('Beschikbare plannen', 'Beschikbare plannen')}:</b> BASIC ({t('basis features', 'basis features')}) · PRO ({t('cloud sync + 5 medewerkers', 'cloud sync + 5 medewerkers')}) · ENTERPRISE ({t('alles + 25 medewerkers', 'alles + 25 medewerkers')}) · LIFETIME ({t('geen vervaldatum', 'geen vervaldatum')})</span>
          </div>
          <DevicesPanel />
          <GeminiKeyPanel />
        </>)}

        {tab === 'cloud' && (<>
          <h3 className="cs-card-title"><Cloud size={16} /> {t('Cloud sync')}</h3>
          {!cloudStatus.licenseActive && (
            <div style={{ padding: 12, background: 'var(--cs-amber-soft)', borderRadius: 6, marginBottom: 14, fontSize: 12.5, color: 'var(--cs-amber)' }}>
              ⚠️ {t('Cloud sync vereist een actieve licentie. Ga naar het Licentie tabblad om te activeren.', 'Cloud sync vereist een actieve licentie. Ga naar het Licentie tabblad om te activeren.')}
            </div>
          )}
          {cloudStatus.enabled ? (
            <div style={{ padding: 16, background: 'var(--cs-green-soft)', borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--cs-green)' }}>
                <CheckCircle size={18} /> {t('Verbonden met', 'Verbonden met')} {cloudStatus.provider}
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5 }}>
                {t('Account', 'Account')}: <b>{cloudStatus.user}</b> {cloudStatus.name && `(${cloudStatus.name})`}<br />
                {t('Geüploade documenten worden automatisch gesynchroniseerd naar je cloud.', 'Geüploade documenten worden automatisch gesynchroniseerd naar je cloud.')}
              </div>
              <button className="cs-btn danger" style={{ marginTop: 12 }} onClick={handleDisconnect}>{t('Cloud loskoppelen')}</button>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
                {t("Verbind je cloud-account om documenten en foto's automatisch op te slaan in jouw cloud-storage. Dit maakt multi-device toegang mogelijk.", "Verbind je cloud-account om documenten en foto's automatisch op te slaan in jouw cloud-storage. Dit maakt multi-device toegang mogelijk.")}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 14 }}>
                <ProviderCard
                  name="Dropbox" icon="📦"
                  available={cloudStatus.providersAvailable?.dropbox}
                  licensed={cloudStatus.licenseActive}
                  onClick={handleConnectDropbox}
                />
                <ProviderCard
                  name="Google Drive" icon="🟢"
                  available={cloudStatus.providersAvailable?.google}
                  licensed={cloudStatus.licenseActive}
                  onClick={handleConnectGoogle}
                />
              </div>
            </>
          )}
        </>)}

        {tab === 'team' && (<>
          <h3 className="cs-card-title"><Users size={16} /> {t('Team / Medewerkers', 'Team / Medewerkers')}</h3>
          <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
  {t('Alle medewerkers binnen')} <b>{user?.garageNaam}</b> {t('zien dezelfde data')}
</p> <table className="cs-table">
            <thead><tr><th>{t('Naam')}</th><th>{t('E-mail')}</th><th>{t('Rol')}</th></tr></thead>
            <tbody>
              {team.map(m => <tr key={m.id}>
                <td style={{ fontWeight: 600 }}>{m.naam || '-'}</td>
                <td>{m.email}</td>
                <td><span className={`cs-badge ${m.role === 'owner' ? 'blue' : 'gray'}`}>{m.role === 'owner' ? t('Eigenaar', 'Eigenaar') : t('Medewerker', 'Medewerker')}</span></td>
              </tr>)}
            </tbody>
          </table>
          {user?.role === 'owner' && (
            <div style={{ marginTop: 18, padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}><UserPlus size={14} style={{ verticalAlign: 'middle' }} /> {t('Medewerker uitnodigen')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 6 }}>
                <input className="cs-input" placeholder={t("Naam")} value={invite.naam} onChange={e => setInvite({ ...invite, naam: e.target.value })} />
                <input className="cs-input" placeholder={t('E-mail', 'E-mail')} type="email" value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} />
                <input className="cs-input" placeholder={t('Tijdelijk wachtwoord (min 6)', 'Tijdelijk wachtwoord (min 6)')} type="text" value={invite.password} onChange={e => setInvite({ ...invite, password: e.target.value })} />
                <button className="cs-btn primary" onClick={async () => {
                  if (!invite.email || invite.password.length < 6) { toast.error(t('Vul e-mail en wachtwoord (min 6) in')); return; }
                  try {
                    await axios.post(`${BASE}/api/auth/invite`, invite);
                    toast.success(t('Medewerker toegevoegd'));
                    setInvite({ email: '', password: '', naam: '' });
                    refreshTeam();
                  } catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
                }}>{t('Uitnodigen', 'Uitnodigen')}</button>
              </div>
            </div>
          )}
        </>)}

        {tab === 'smtp' && (<>
          <h3 className="cs-card-title"><Mail size={16} /> {t('Mijn SMTP instellingen', 'Mijn SMTP instellingen')}</h3>
          <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
            {t('Vul je e-mail credentials in om facturen en herinneringen vanuit jouw eigen e-mailadres te versturen.', 'Vul je e-mail credentials in om facturen en herinneringen vanuit jouw eigen e-mailadres te versturen.')}
            <br /><b>{t('Gmail tip:', 'Gmail tip:')}</b> {t('gebruik een', 'gebruik een')} <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ color: 'var(--cs-accent)' }}>App Password</a> {t('(geen normaal wachtwoord). Vereist 2FA op je Google account.', '(geen normaal wachtwoord). Vereist 2FA op je Google account.')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, maxWidth: 600 }}>
            <div><label className="cs-section-title">SMTP Server</label><input className="cs-input" value={smtp.host} onChange={e => setSmtp({ ...smtp, host: e.target.value })} /></div>
            <div><label className="cs-section-title">Poort</label><input type="number" className="cs-input" value={smtp.port} onChange={e => setSmtp({ ...smtp, port: +e.target.value })} /></div>
            <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('E-mail / gebruikersnaam')}</label><input className="cs-input" placeholder="jouw@gmail.com" value={smtp.user} onChange={e => setSmtp({ ...smtp, user: e.target.value })} /></div>
            <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('App password')}</label><input type="password" className="cs-input" placeholder="abcd efgh ijkl mnop" value={smtp.password} onChange={e => setSmtp({ ...smtp, password: e.target.value })} /></div>
            <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Afzendernaam (optioneel)')}</label><input className="cs-input" placeholder="Garage Janssen" value={smtp.from_name} onChange={e => setSmtp({ ...smtp, from_name: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button className="cs-btn primary" onClick={async () => {
              try { await axios.post(`${BASE}/api/auth/smtp`, smtp); toast.success(t('SMTP opgeslagen')); }
              catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
            }}><Save size={14} /> {t('Opslaan')}</button>
            <button className="cs-btn" onClick={async () => {
              try {
                const r = await axios.post(`${BASE}/api/auth/smtp/test`, { creds: smtp, to: user?.email });
                toast.success(`Testbericht verzonden naar ${r.data.to}`);
              } catch (e) { toast.error(t('SMTP test mislukt'), { description: e.response?.data?.detail || e.message }); }
            }}><Send size={14} /> {t('Test verzenden', 'Test verzenden')}</button>
          </div>
          {user?.smtpConfigured && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--cs-green)' }}><CheckCircle size={12} style={{ verticalAlign: 'middle' }} /> {t('Er staan al credentials opgeslagen voor dit account.')}</div>}
        </>)}

        {tab === 'betalen' && (<>
          <h3 className="cs-card-title"><CreditCard size={16} /> {t('Online betalen (Stripe)', 'Online betalen (Stripe)')}</h3>
          <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
            {t('Laat klanten je facturen direct online betalen met iDEAL of creditcard. Er wordt een betaallink toegevoegd aan de factuur-e-mail; betaalde facturen worden automatisch op "Betaald" gezet.', 'Laat klanten je facturen direct online betalen met iDEAL of creditcard. Er wordt een betaallink toegevoegd aan de factuur-e-mail; betaalde facturen worden automatisch op "Betaald" gezet.')}
          </p>
          <div style={{ padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8, maxWidth: 600 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }} data-testid="toggle-online-betalen">
              <input type="checkbox" checked={!!betaling.stripeOnlineBetalen} style={{ width: 18, height: 18 }}
                onChange={async (e) => {
                  const en = e.target.checked;
                  setBetaling({ ...betaling, stripeOnlineBetalen: en });
                  try { const r = await carsysApi.setBetalingInstellingen({ stripeOnlineBetalen: en }); setBetaling(r); toast.success(en ? t('Online betalen aan', 'Online betalen aan') : t('Online betalen uit', 'Online betalen uit')); }
                  catch (err) { toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message }); }
                }} />
              {betaling.stripeOnlineBetalen ? t('Online betalen ingeschakeld', 'Online betalen ingeschakeld') : t('Online betalen uitgeschakeld', 'Online betalen uitgeschakeld')}
            </label>
          </div>

          <div style={{ marginTop: 16, maxWidth: 600 }}>
            <label className="cs-section-title">{t('Jouw Stripe Secret Key', 'Jouw Stripe Secret Key')}</label>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 6 }}>
              {t('Het geld komt rechtstreeks op jouw eigen Stripe-account. Vind je sleutel op', 'Het geld komt rechtstreeks op jouw eigen Stripe-account. Vind je sleutel op')} <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" style={{ color: 'var(--cs-accent)' }}>dashboard.stripe.com/apikeys</a>.
            </div>
            {betaling.stripeKeyConfigured && (
              <div style={{ fontSize: 12, color: 'var(--cs-green)', marginBottom: 8 }} data-testid="stripe-key-status"><CheckCircle size={12} style={{ verticalAlign: 'middle' }} /> {t('Er is een Stripe-sleutel ingesteld voor deze garage.', 'Er is een Stripe-sleutel ingesteld voor deze garage.')}</div>
            )}
            <input type="password" className="cs-input" placeholder="sk_live_... of sk_test_..." value={stripeKeyInput} onChange={e => setStripeKeyInput(e.target.value)} data-testid="input-stripe-key" />
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="cs-btn primary" disabled={betalingBusy || !stripeKeyInput.trim()} data-testid="btn-save-stripe-key" onClick={async () => {
                setBetalingBusy(true);
                try { const r = await carsysApi.setBetalingInstellingen({ stripeSecretKey: stripeKeyInput.trim() }); setBetaling(r); setStripeKeyInput(''); toast.success(t('Stripe-sleutel opgeslagen', 'Stripe-sleutel opgeslagen')); }
                catch (err) { toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message }); }
                finally { setBetalingBusy(false); }
              }}><Save size={14} /> {t('Sleutel opslaan', 'Sleutel opslaan')}</button>
              {betaling.stripeKeyConfigured && (
                <button className="cs-btn warning" disabled={betalingBusy} data-testid="btn-clear-stripe-key" onClick={async () => {
                  setBetalingBusy(true);
                  try { const r = await carsysApi.setBetalingInstellingen({ stripeSecretKey: '__clear__' }); setBetaling(r); toast.success(t('Stripe-sleutel verwijderd', 'Stripe-sleutel verwijderd')); }
                  catch (err) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt'), { description: err.response?.data?.detail || err.message }); }
                  finally { setBetalingBusy(false); }
                }}><Trash2 size={14} /> {t('Sleutel verwijderen', 'Sleutel verwijderen')}</button>
              )}
            </div>
          </div>
        </>)}

        {tab === 'reviews' && (<>
          <h3 className="cs-card-title"><Star size={16} /> {t('Reviews (Google-recensies)', 'Reviews (Google-recensies)')}</h3>
          <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
            {t('Vraag klanten automatisch om een Google-recensie na een betaalde factuur. Meer recensies = meer vertrouwen en nieuwe klanten.', 'Vraag klanten automatisch om een Google-recensie na een betaalde factuur. Meer recensies = meer vertrouwen en nieuwe klanten.')}
          </p>
          <div style={{ maxWidth: 600 }}>
            <label className="cs-section-title">{t('Jouw Google-reviewlink', 'Jouw Google-reviewlink')}</label>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 6 }}>
              {t('Vind je link via je Google Bedrijfsprofiel → "Vraag om reviews". Plak hier de volledige https-link.', 'Vind je link via je Google Bedrijfsprofiel → "Vraag om reviews". Plak hier de volledige https-link.')}
            </div>
            <input className="cs-input" placeholder="https://g.page/r/..." value={review.googleReviewUrl} onChange={e => setReview({ ...review, googleReviewUrl: e.target.value })} data-testid="input-review-url" />

            <div style={{ marginTop: 16, padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }} data-testid="toggle-auto-review">
                <input type="checkbox" checked={!!review.autoReview} style={{ width: 18, height: 18 }}
                  onChange={e => setReview({ ...review, autoReview: e.target.checked })} />
                {t('Automatisch een reviewverzoek sturen na betaling', 'Automatisch een reviewverzoek sturen na betaling')}
              </label>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                {t('Verstuur', 'Verstuur')}
                <input type="number" min="0" max="90" className="cs-input" style={{ width: 80 }} value={review.reviewDagen}
                  onChange={e => setReview({ ...review, reviewDagen: parseInt(e.target.value) || 0 })} data-testid="input-review-dagen" />
                {t('dagen nadat een factuur is betaald', 'dagen nadat een factuur is betaald')}
              </div>
            </div>

            <button className="cs-btn primary" style={{ marginTop: 14 }} disabled={reviewBusy} data-testid="btn-save-review" onClick={async () => {
              setReviewBusy(true);
              try { const r = await carsysApi.setReviewInstellingen(review); setReview(r); toast.success(t('Review-instellingen opgeslagen', 'Review-instellingen opgeslagen')); }
              catch (err) { toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message }); }
              finally { setReviewBusy(false); }
            }}><Save size={14} /> {t('Opslaan')}</button>

            <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--cs-text-muted)' }}>
              💡 {t('Tip: je kunt ook handmatig een review vragen via de knop "Vraag review" op een factuur — inclusief QR-code voor in de werkplaats.', 'Tip: je kunt ook handmatig een review vragen via de knop "Vraag review" op een factuur — inclusief QR-code voor in de werkplaats.')}
            </div>
          </div>
        </>)}

        {tab === 'team_OLD' && (<></>)}

        {tab === 'backup' && (<>
          <h3 className="cs-card-title"><Database size={16} /> {t('Back-up')}</h3>
          <div style={{ padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('1. Download back-up', '1. Download back-up')}</div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
              {t('backup.download.desc', 'Download een complete JSON snapshot van alle data (klanten, voertuigen, bonnen, planning, voorraad, betalingen, etc). Bewaar dit bestand op een veilige plek (Dropbox / Google Drive / externe schijf).')}
            </div>
            <button data-testid="btn-backup-download" className="cs-btn primary" onClick={downloadBackup}><Download size={14} /> {t('Download back-up', 'Download back-up')}</button>
          </div>

          <div style={{ padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('2. Back-up herstellen (import)', '2. Back-up herstellen (import)')}</div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
              {t('backup.restore.desc', 'Upload een eerder gedownload back-up bestand om je data terug te zetten.')}
              <br /><b style={{ color: 'var(--cs-red)' }}>{t('Let op:', 'Let op:')}</b> {t('backup.restore.warning', 'alle huidige data in jouw garage wordt vervangen door de inhoud van het bestand.')}
            </div>
            <label className="cs-btn warning" style={{ cursor: 'pointer' }} data-testid="btn-backup-restore-label">
              <Upload size={14} /> {t('Back-up bestand selecteren', 'Back-up bestand selecteren')} (.json)
              <input data-testid="input-backup-restore" type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={restoreBackup} />
            </label>
          </div>

          <div style={{ padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('3. Back-up naar cloud (Pro/Enterprise)', '3. Back-up naar cloud (Pro/Enterprise)')}</div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
              {t('backup.cloud.desc', 'Stuur direct een complete back-up naar je gekoppelde cloud (Dropbox / Google Drive). Het bestand verschijnt in je')}
              {' '}<b>Apps/CarSys</b> {t('of', 'of')} <b>/CarSys</b> {t('map.', 'map.')}
            </div>
            {!cloudStatus.licenseActive && (
              <div style={{ fontSize: 12, color: 'var(--cs-amber)', marginBottom: 10 }}>
                ⚠ {t('backup.cloud.licenseRequired', 'Licentie vereist. Activeer eerst een PRO of ENTERPRISE licentie bij')} <b>{t('Licentie')}</b>.
              </div>
            )}
            {cloudStatus.licenseActive && !cloudStatus.enabled && (
              <div style={{ fontSize: 12, color: 'var(--cs-amber)', marginBottom: 10 }}>
                ⚠ {t('backup.cloud.notLinked', 'Nog geen cloud gekoppeld. Ga naar')} <b>{t('Cloud sync')}</b> {t('backup.cloud.notLinked.suffix', 'tabblad en koppel Dropbox of Google Drive.')}
              </div>
            )}
            {cloudStatus.licenseActive && cloudStatus.enabled && (
              <>
                <div style={{ fontSize: 12, color: 'var(--cs-green)', marginBottom: 10 }}>
                  ✓ {t('backup.cloud.linked', 'Cloud gekoppeld:')} <b>{cloudStatus.provider}</b> ({cloudStatus.user || cloudStatus.name})
                </div>
                <button data-testid="btn-backup-cloud" className="cs-btn primary" onClick={async () => {
                  try {
                    const r = await carsysApi.backupToCloud();
                    toast.success(t('Cloud back-up gelukt', `${r.records} records → ${r.provider}/${r.filename}`));
                    carsysApi.getCloudStatus().then(setCloudStatus).catch(() => {});
                  } catch (err) {
                    toast.error(t('Cloud back-up mislukt'), { description: err.response?.data?.detail || err.message });
                  }
                }}><Cloud size={14} /> {t('Back-up nu naar cloud', 'Back-up nu naar cloud')}</button>
                {cloudStatus.lastCloudBackup && (
                  <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 8 }}>
                    {t('Laatste cloud back-up:', 'Laatste cloud back-up:')} {new Date(cloudStatus.lastCloudBackup).toLocaleString()}
                    {cloudStatus.lastCloudBackupAuto && <span style={{ color: 'var(--cs-blue)', marginLeft: 4 }}>({t('automatisch', 'automatisch')})</span>}
                    {cloudStatus.lastCloudBackupFilename && <> · <code>{cloudStatus.lastCloudBackupFilename}</code></>}
                  </div>
                )}
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--cs-border)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }} data-testid="toggle-auto-backup">
                    <input type="checkbox"
                      checked={cloudStatus.autoBackupEnabled !== false}
                      onChange={async (e) => {
                        const en = e.target.checked;
                        try {
                          await carsysApi.setAutoBackup(en);
                          setCloudStatus({ ...cloudStatus, autoBackupEnabled: en });
                          toast.success(en ? t('Auto-backup aan', 'Auto-backup aan') : t('Auto-backup uit', 'Auto-backup uit'));
                        } catch (err) {
                          toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message });
                        }
                      }}
                    />
                    <span><b>{t('Automatische nachtelijke back-up', 'Automatische nachtelijke back-up')}</b> — {t('backup.auto.desc', 'wij sturen elke 24u automatisch een verse back-up naar je cloud-map.')}</span>
                  </label>
                </div>
              </>
            )}
          </div>
        </>)}

        {tab === 'meldingen' && (<>
          <h3 className="cs-card-title"><Bell size={16} /> {t('Meldingen instellen', 'Meldingen instellen')}</h3>
          <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', margin: '0 0 12px 0' }}>
            {t('Meldingen worden automatisch elke nacht gegenereerd op basis van APK-datum, openstaande bonnen en geplande afspraken. Klik hieronder om ze handmatig nu te verversen.',
               'Meldingen worden automatisch elke nacht gegenereerd op basis van APK-datum, openstaande bonnen en geplande afspraken. Klik hieronder om ze handmatig nu te verversen.')}
          </p>
          <button className="cs-btn primary" onClick={async () => {
            try {
              const r = await refreshMeldingen();
              toast.success(t('{count} meldingen geregenereerd', '{count} meldingen geregenereerd').replace('{count}', r.count));
            } catch { toast.error(t('Mislukt')); }
          }} data-testid="btn-regenerate-meldingen">
            {t('Meldingen nu opnieuw genereren', 'Meldingen nu opnieuw genereren')}
          </button>
        </>)}

        {tab === 'maillog' && (<>
          <h3 className="cs-card-title"><Mail size={16} /> {t('Mail log', 'Mail log')}</h3>
          <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
            {t('Alle bevestigingen, herinneringen en facturen die via de app zijn verstuurd.', 'Alle bevestigingen, herinneringen en facturen die via de app zijn verstuurd.')}
          </div>
          {mailLog.length === 0 ? <div className="cs-empty">{t('Nog geen e-mails verstuurd.', 'Nog geen e-mails verstuurd.')}</div> : (
            <table className="cs-table">
              <thead><tr><th>{t('Datum')}</th><th>{t('Type')}</th><th>{t('Aan')}</th><th>{t('Onderwerp')}</th><th>{t('Status')}</th></tr></thead>
              <tbody>{mailLog.map(m => {
                const typeLabel = t(m.type, m.type); // bon/herinnering/bevestiging worden vertaald als er een entry in i18n is
                const isReal = m.status && !String(m.status).toLowerCase().includes('mock');
                return (
                  <tr key={m.id}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{m.datum}</td>
                    <td><span className="cs-badge blue">{typeLabel}</span></td>
                    <td>{m.aan}</td>
                    <td>{m.onderwerp}</td>
                    <td><span className={`cs-badge ${isReal ? 'green' : 'amber'}`} style={{ fontSize: 10 }}>{m.status || '—'}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </>)}
      </div>
    </div>
  );
}

function ProviderCard({ name, icon, available, licensed, comingSoon, onClick }) {
  const { t } = useT();
  const disabled = !available || !licensed || comingSoon;
  return (
    <div style={{
      padding: 16, border: `1px solid ${disabled ? 'var(--cs-border)' : 'var(--cs-accent)'}`,
      borderRadius: 10, background: disabled ? '#fafbfc' : '#fff',
      textAlign: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1, transition: 'all 140ms ease',
    }} onClick={() => !disabled && onClick && onClick()}>
      <div style={{ fontSize: 32, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{name}</div>
      <div style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>
        {comingSoon ? t('Binnenkort beschikbaar', 'Binnenkort beschikbaar') : !licensed ? t('Licentie vereist') : !available ? t('Niet geconfigureerd', 'Niet geconfigureerd') : t('Klik om te verbinden', 'Klik om te verbinden')}
      </div>
      {!disabled && (
        <button className="cs-btn primary" style={{ marginTop: 10, padding: '6px 16px' }} onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}>
          <LinkIcon size={12} /> {t('Verbind', 'Verbind')}
        </button>
      )}
    </div>
  );
}


function LocatiesManager() {
  const { t } = useT();
  const { gegevens, updateGegevens } = useData();
  const [nieuw, setNieuw] = useState('');
  const [busy, setBusy] = useState(false);
  const locaties = gegevens?.locaties || [];

  const save = async (lijst) => {
    setBusy(true);
    try { await updateGegevens({ ...gegevens, locaties: lijst }); }
    catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const add = async () => {
    const v = nieuw.trim();
    if (!v) return;
    if (locaties.some(l => l.toLowerCase() === v.toLowerCase())) { toast.error(t('Bestaat al', 'Bestaat al')); return; }
    setNieuw('');
    await save([...locaties, v]);
  };
  const remove = (l) => save(locaties.filter(x => x !== l));

  return (
    <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="instellingen-locaties">
      <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <MapPin size={14} /> {t('Werkplek- & magazijnlocaties', 'Werkplek- & magazijnlocaties')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginBottom: 12 }}>
        {t('Beheer locaties zoals Brug 1, Brug 2, Magazijn A of Schap 3. Ze verschijnen als keuzelijst bij onderdelen en in de werkplaatsplanning.', 'Beheer locaties zoals Brug 1, Brug 2, Magazijn A of Schap 3. Ze verschijnen als keuzelijst bij onderdelen en in de werkplaatsplanning.')}
      </div>
      <div style={{ display: 'flex', gap: 6, maxWidth: 480, marginBottom: 12 }}>
        <input className="cs-input" placeholder={t('Bijv. Brug 1', 'Bijv. Brug 1')} value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid="locatie-input" />
        <button className="cs-btn primary" onClick={add} disabled={busy} data-testid="locatie-add"><Plus size={14} /> {t('Toevoegen', 'Toevoegen')}</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} data-testid="locatie-list">
        {locaties.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{t('Nog geen locaties toegevoegd.', 'Nog geen locaties toegevoegd.')}</span>}
        {locaties.map(l => (
          <span key={l} data-testid={`locatie-chip-${l}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 999, padding: '5px 8px 5px 12px', fontSize: 13, fontWeight: 600 }}>
            <MapPin size={12} /> {l}
            <button onClick={() => remove(l)} data-testid={`locatie-del-${l}`} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', padding: 0 }}><Trash2 size={13} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function GeminiKeyPanel() {
  const { t } = useT();
  const { gegevens, refreshAll } = useData();
  const isSet = !!gegevens?.geminiKeySet;
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!key.trim()) { toast.error(t('Voer je Gemini API-sleutel in', 'Voer je Gemini API-sleutel in')); return; }
    setBusy(true);
    try {
      await axios.put(`${BASE}/api/gegevens/gemini-key`, { key: key.trim() });
      toast.success(t('Gemini-sleutel opgeslagen en getest ✓', 'Gemini-sleutel opgeslagen en getest ✓'));
      setKey('');
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Opslaan mislukt', 'Opslaan mislukt'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog(t('Gemini-sleutel verwijderen? AI-functies werken daarna niet meer.', 'Gemini-sleutel verwijderen? AI-functies werken daarna niet meer.')))) return;
    setBusy(true);
    try {
      await axios.delete(`${BASE}/api/gegevens/gemini-key`);
      toast.success(t('Sleutel verwijderd', 'Sleutel verwijderd'));
      await refreshAll();
    } catch { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt')); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 24, padding: 16, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="instellingen-gemini-key">
      <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Sparkles size={15} /> {t('AI / Gemini API-sleutel', 'AI / Gemini API-sleutel')}
        {isSet
          ? <span className="cs-badge green" style={{ marginLeft: 6 }} data-testid="gemini-key-status-set"><CheckCircle size={12} /> {t('Actief', 'Actief')}</span>
          : <span className="cs-badge" style={{ marginLeft: 6, background: 'var(--cs-amber-soft)', color: 'var(--cs-amber)' }} data-testid="gemini-key-status-unset"><XCircle size={12} /> {t('Niet ingesteld', 'Niet ingesteld')}</span>}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginBottom: 12, lineHeight: 1.6 }}>
        {t('Alle AI-functies (advertenties, foutcode-uitleg, offerte-assistent, foto-herkenning, taxatie, compatibiliteit, papieren scannen en diagnoserapporten) draaien op jouw eigen, gratis Google Gemini-sleutel — je betaalt zelf niets extra en alles blijft binnen je eigen Google-account.', 'Alle AI-functies (advertenties, foutcode-uitleg, offerte-assistent, foto-herkenning, taxatie, compatibiliteit, papieren scannen en diagnoserapporten) draaien op jouw eigen, gratis Google Gemini-sleutel — je betaalt zelf niets extra en alles blijft binnen je eigen Google-account.')}
        <br /><b>{t('Zonder ingestelde sleutel werken de AI-knoppen niet.', 'Zonder ingestelde sleutel werken de AI-knoppen niet.')}</b>
      </div>
      <ol style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', margin: '0 0 12px 0', paddingLeft: 18, lineHeight: 1.9 }}>
        <li>{t('Ga naar', 'Ga naar')} <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{ color: 'var(--cs-blue, #2563eb)', fontWeight: 600 }}>aistudio.google.com/apikey <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /></a> {t('en log in met je Google-account.', 'en log in met je Google-account.')}</li>
        <li>{t("Klik op 'Create API key' (volledig gratis) en kopieer de volledige sleutel — deze begint met AQ. of AIza…", "Klik op 'Create API key' (volledig gratis) en kopieer de volledige sleutel — deze begint met AQ. of AIza…")}</li>
        <li>{t('Plak de sleutel hieronder en klik op Opslaan. We testen hem meteen voordat we hem (versleuteld) bewaren.', 'Plak de sleutel hieronder en klik op Opslaan. We testen hem meteen voordat we hem (versleuteld) bewaren.')}</li>
      </ol>
      <div style={{ display: 'flex', gap: 6, maxWidth: 560 }}>
        <input className="cs-input" type="password" autoComplete="off" placeholder={isSet ? t('•••••••• (nieuwe sleutel invoeren om te wijzigen)', '•••••••• (nieuwe sleutel invoeren om te wijzigen)') : 'AQ.... of AIza...'} value={key} onChange={e => setKey(e.target.value)} data-testid="gemini-key-input" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }} />
        <button className="cs-btn primary" onClick={save} disabled={busy} data-testid="gemini-key-save" style={{ whiteSpace: 'nowrap' }}>{busy ? t('Bezig…', 'Bezig…') : t('Opslaan', 'Opslaan')}</button>
        {isSet && <button className="cs-btn danger" onClick={remove} disabled={busy} data-testid="gemini-key-delete" style={{ whiteSpace: 'nowrap' }} title={t('Verwijderen', 'Verwijderen')}><Trash2 size={14} /></button>}
      </div>
    </div>
  );
}

const DEFAULT_AD_CTA = 'Meer onderdelen & occasions nodig? Bekijk mijn complete aanbod in mijn online showroom:';

function ShowroomAdSetting() {
  const { t } = useT();
  const { gegevens, updateGegevens } = useData();
  const aan = !!gegevens?.showroomLinkInAds;
  const [cta, setCta] = useState(gegevens?.showroomAdCta || '');
  useEffect(() => { setCta(gegevens?.showroomAdCta || ''); }, [gegevens?.showroomAdCta]);

  const toggle = async () => {
    try { await updateGegevens({ ...gegevens, showroomLinkInAds: !aan }); toast.success(t('Opgeslagen', 'Opgeslagen')); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const saveCta = async () => {
    if ((gegevens?.showroomAdCta || '') === cta) return;
    try { await updateGegevens({ ...gegevens, showroomAdCta: cta }); toast.success(t('Tekst opgeslagen', 'Tekst opgeslagen')); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--cs-border)' }} data-testid="showroom-ad-setting">
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={aan} onChange={toggle} data-testid="showroom-ad-toggle" style={{ marginTop: 3, width: 16, height: 16 }} />
        <span>
          <span style={{ fontWeight: 600 }}>{t('Showroom-link automatisch onder advertenties zetten', 'Showroom-link automatisch onder advertenties zetten')}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--cs-text-muted)', marginTop: 2 }}>
            {t('Voegt je showroom-link + onderstaande tekst toe aan AI-advertenties voor Facebook en Forum. Niet op Marktplaats — externe links zijn daar niet toegestaan.', 'Voegt je showroom-link + onderstaande tekst toe aan AI-advertenties voor Facebook en Forum. Niet op Marktplaats — externe links zijn daar niet toegestaan.')}
          </span>
        </span>
      </label>
      {aan && (
        <div style={{ marginTop: 10, maxWidth: 720 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', display: 'block', marginBottom: 4 }}>{t('Oproep-tekst (CTA)', 'Oproep-tekst (CTA)')}</label>
          <textarea className="cs-input" rows={2} value={cta} onChange={e => setCta(e.target.value)} onBlur={saveCta}
            placeholder={DEFAULT_AD_CTA} data-testid="showroom-ad-cta" style={{ resize: 'vertical', fontSize: 13 }} />
        </div>
      )}
    </div>
  );
}

function ShowroomThemePanel() {
  const { t } = useT();
  const { gegevens, updateGegevens, refreshAll } = useData();
  const [accent, setAccent] = useState(gegevens?.showroomAccent || '#7c3aed');
  const [theme, setTheme] = useState(gegevens?.showroomTheme || 'dark');
  const [busy, setBusy] = useState(false);
  const heeftBanner = !!gegevens?.showroomBannerPath;
  useEffect(() => { setAccent(gegevens?.showroomAccent || '#7c3aed'); setTheme(gegevens?.showroomTheme || 'dark'); }, [gegevens?.showroomAccent, gegevens?.showroomTheme]);

  const saveTheme = async () => {
    setBusy(true);
    try { await updateGegevens({ ...gegevens, showroomAccent: accent, showroomTheme: theme }); toast.success(t('Showroom-stijl opgeslagen', 'Showroom-stijl opgeslagen')); }
    catch { toast.error(t('Opslaan mislukt', 'Opslaan mislukt')); }
    finally { setBusy(false); }
  };
  const onBanner = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { await carsysApi.uploadShowroomBanner(file); toast.success(t('Banner geüpload', 'Banner geüpload')); await refreshAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || t('Upload mislukt', 'Upload mislukt')); }
    finally { setBusy(false); if (e.target) e.target.value = ''; }
  };
  const delBanner = async () => {
    setBusy(true);
    try { await carsysApi.deleteShowroomBanner(); toast.success(t('Banner verwijderd', 'Banner verwijderd')); await refreshAll(); }
    catch { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt')); }
    finally { setBusy(false); }
  };

  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', marginBottom: 5 };
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--cs-border)' }} data-testid="showroom-theme-panel">
      <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Sparkles size={15} /> {t('Showroom-stijl (kleuren, thema & banner)', 'Showroom-stijl (kleuren, thema & banner)')}</div>
      <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginBottom: 14 }}>{t('Pas de uitstraling van je publieke showroom én boekingspagina aan. Je logo stel je in onder Bon layout.', 'Pas de uitstraling van je publieke showroom én boekingspagina aan. Je logo stel je in onder Bon layout.')}</div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl}>{t('Accentkleur', 'Accentkleur')}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="color" value={accent} onChange={e => setAccent(e.target.value)} data-testid="showroom-accent" style={{ width: 46, height: 38, border: '1px solid var(--cs-border)', borderRadius: 8, padding: 2, cursor: 'pointer', background: '#fff' }} />
            <input className="cs-input" value={accent} onChange={e => setAccent(e.target.value)} style={{ width: 110, fontFamily: 'ui-monospace, monospace', fontSize: 13 }} />
          </div>
        </div>
        <div>
          <label style={lbl}>{t('Thema', 'Thema')}</label>
          <select className="cs-input" value={theme} onChange={e => setTheme(e.target.value)} data-testid="showroom-theme" style={{ width: 150 }}>
            <option value="dark">{t('Donker', 'Donker')}</option>
            <option value="light">{t('Licht', 'Licht')}</option>
          </select>
        </div>
        <button className="cs-btn primary" onClick={saveTheme} disabled={busy} data-testid="showroom-theme-save">{busy ? t('Bezig…', 'Bezig…') : t('Stijl opslaan', 'Stijl opslaan')}</button>
      </div>
      <div style={{ marginTop: 16 }}>
        <label style={lbl}>{t('Banner-afbeelding (bovenaan showroom)', 'Banner-afbeelding (bovenaan showroom)')}</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="cs-btn" data-testid="showroom-banner-upload" style={{ cursor: 'pointer' }}>
            <Upload size={14} /> {heeftBanner ? t('Banner vervangen', 'Banner vervangen') : t('Banner uploaden', 'Banner uploaden')}
            <input type="file" accept="image/*" hidden onChange={onBanner} disabled={busy} />
          </label>
          {heeftBanner && <button className="cs-btn danger" onClick={delBanner} disabled={busy} data-testid="showroom-banner-delete"><Trash2 size={14} /> {t('Verwijderen', 'Verwijderen')}</button>}
          {heeftBanner && <span style={{ fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}>{t('Banner ingesteld ✓', 'Banner ingesteld ✓')}</span>}
        </div>
      </div>
    </div>
  );
}

